/**
 * ¿El que mira es un almacén que es SÓLO almacén? (founder, 23-oct-2026)
 *
 * Almacén 1 guarda y despacha la mercadería de Suplemax: marca blanca. El founder: «Almacén 1 no debe ver precios de a
 * cómo se venden los productos… si ve los precios publicados va a querer subirme los precios. Necesito que ellos vean
 * sólo el despacho y su liquidación». Un negocio del plan «almacén» que no publica catálogo propio no tiene por qué ver
 * un solo monto de la mercadería ajena: ni el mayorista del encargo ni el de venta.
 *
 * 🪤 Es la misma consulta que usa el menú (`CLAVES_DROP.soyProveedor`): no agrega viajes a la base.
 */
import { useAuth, useFeatureFlags } from '@smarty/shared';
import { useDatosCacheados } from '@/hooks/useDatosCacheados';
import { CLAVES_DROP } from '@/lib/dropshipping/clavesDeCache';
import { proveedorDelTenant } from '@/lib/products/smartyDropApi';

/**
 * El mismo hecho, RESUELTO: `null` mientras la base no contestó si es proveedor.
 *
 * 🪤 PARA EL PORTÓN DE RUTAS, no para esconder. Mientras se consulta, `useSoloAlmacen()` dice «sí» (lo seguro para
 *    tapar montos: un parpadeo de menos, nunca de más). Pero si el portón creyera eso, a North Express o Suplemax
 *    —almacenes que SÍ son proveedores— los sacaría de su Catálogo al recargar la pantalla. El portón espera.
 */
export function useSoloAlmacenResuelto(): boolean | null {
  const { tenantId } = useAuth();
  const { planType } = useFeatureFlags();
  const esPlanAlmacen = planType === 'almacen';
  const { datos: soyProveedor, cargando } = useDatosCacheados<boolean>({
    clave: [CLAVES_DROP.soyProveedor, tenantId],
    activo: !!tenantId && esPlanAlmacen,
    frescura: 5 * 60_000,
    traer: async () => !!(await proveedorDelTenant(tenantId!)),
  });
  if (!esPlanAlmacen) return false;
  if (cargando || soyProveedor === undefined) return null;
  return !soyProveedor;
}

export function useSoloAlmacen(): boolean {
  const { planType } = useFeatureFlags();
  const resuelto = useSoloAlmacenResuelto();
  // Mientras no se sabe, se esconde: el plan almacen tapa hasta que la base diga que es proveedor.
  return resuelto ?? planType === 'almacen';
}
